import React, { useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { makeGetTask } from 'selectors/tasks';
import { makeGetRecursionRule } from 'selectors/recursion-rules';

import { createRecursionRule, updateRecursionRule, removeRecursionRule } from 'actions/recursion-rules';

import RecursionRuleContainer from 'containers/recursion-rule-container';

import ResourceRepeatPopout from 'components/resource-repeat-popout';

const TaskRecursionPicker = ({ id, taskRule, rule, taskProject, taskDueDate, taskStartDate, withTitle, xAlign, defaultXPosition, inline, autoShow, onShow, onHide, onComplete, onCancel }) => {
    const { t } = useTranslation('task');

    const handleRepeatTaskUpdate = useCallback(({ type, trigger, schedule, till, cron, bi_weekly, options, fields }) => {
        if (taskRule) {
            updateRecursionRule(taskRule, {
                type,
                trigger,
                schedule,
                till,
                cron,
                bi_weekly,
                options,
                fields
            });

            onComplete && onComplete();
            return;
        }

        createRecursionRule({
            source_task: id,
            type,
            trigger,
            schedule,
            till,
            cron,
            bi_weekly,
            options,
            fields
        });

        onComplete && onComplete();
    }, [id, taskRule, onComplete]);

    const offsetFields = useMemo(() => {
        return [
            { type: 'due_date', value: taskDueDate || null },
            { type: 'start_date', value: taskStartDate || null }
        ];
    }, [taskDueDate, taskStartDate]);

    const handleRepeatDisable = useCallback(() => {
        removeRecursionRule(taskRule);

        onComplete && onComplete();
    }, [id, taskRule, onComplete]);

    return (
        <>
            <RecursionRuleContainer id={taskRule} />

            {
                !taskRule || rule ? (
                    <ResourceRepeatPopout
                        key={id}
                        resourceType="task"
                        title={ withTitle && t('actions.Repeat this Task') }
                        projectId={taskProject}
                        xAlign={xAlign}
                        defaultXPosition={defaultXPosition}
                        rule={rule}
                        defaultFields={offsetFields}
                        inline={inline}
                        autoShow={autoShow}
                        onChange={handleRepeatTaskUpdate}
                        onDisable={handleRepeatDisable}
                        onShow={onShow}
                        onHide={onHide}
                        onCancel={onCancel}
                    >
                        <div className="popout-toggle" />
                    </ResourceRepeatPopout>
                ) : null
            }
        </>
    );
}

const makeMapStateToProps = () => {
    const getTask = makeGetTask();
    const getRecursionRule = makeGetRecursionRule();

    return ({ tasks, milestones, recursionRules }, ownProps) => {
        const task = getTask(tasks.items, ownProps.id);
        const taskRule = task?.rule;

        return {
            taskRule,
            taskProject: task?.project,
            taskStartDate: task?.start_date,
            taskDueDate: task?.due_date,
            rule: getRecursionRule(recursionRules.items, taskRule)
        };
    };
};

export default connect(makeMapStateToProps)(React.memo(TaskRecursionPicker));
